"use client";

import { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Container,
  Chip,
  Avatar,
  Divider,
  Paper,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Fade,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (!userData) {
      navigate("/login");
    } else {
      fetchOrders(userData.user_id);
    }
  }, [navigate]);

  useEffect(() => {
    const filtered = orders.filter((order) =>
      order.status.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOrders(filtered);
  }, [orders, searchTerm]);

  const fetchOrders = async (userId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://namami-infotech.com/EvaraBackend/src/order/get_orders.php?user_id=${userId}`
      );
      if (response.data.success) {
        setOrders(response.data.orders);
        setError("");
      } else {
        setError("Failed to fetch orders.");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError("Error loading orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "success";
      case "shipped":
      case "processing":
        return "info";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return <CheckCircleIcon />;
      case "shipped":
        return <ShippingIcon />;
      case "processing":
        return <PendingIcon />;
      case "cancelled":
        return <CancelIcon />;
      default:
        return <ReceiptIcon />;
    }
  };

  const handleImageClick = (imageData) => {
    setSelectedImage(imageData);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Skeleton
            variant="text"
            width={300}
            height={60}
            sx={{ mx: "auto" }}
          />
        </Box>
        <Grid container spacing={3}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              color: "white",
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              mb: 2,
            }}
          >
            Order History
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.9)",
              mb: 3,
            }}
          >
            Track and manage all your orders
          </Typography>

          {/* Search Bar */}
          <Paper
            sx={{
              maxWidth: 500,
              mx: "auto",
              mb: 3,
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <TextField
              fullWidth
              placeholder="Search orders by ID or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#008080" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  border: "none",
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </Paper>
        </Box>

        {/* Error Alert */}
       

        {/* Orders Grid */}
        <Grid container spacing={3}>
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order, index) => (
              <Grid item xs={12} lg={6} key={order.orderId}>
                <Fade in timeout={300 + index * 100}>
                  <Card
                    sx={{
                      background: "rgba(255,255,255,0.95)",
                      backdropFilter: "blur(10px)",
                      borderRadius: 3,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Order Header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            sx={{ bgcolor: "#008080", width: 32, height: 32 }}
                          >
                            <ShoppingBagIcon fontSize="small" />
                          </Avatar>
                          <Typography
                            variant="h6"
                            sx={{ color: "#008080", fontWeight: "bold" }}
                          >
                            #{order.orderId}
                          </Typography>
                        </Box>
                        <Chip
                          icon={getStatusIcon(order.status)}
                          label={order.status}
                          color={getStatusColor(order.status)}
                          variant="filled"
                          sx={{ fontWeight: "bold" }}
                        />
                      </Box>

                      {/* Order Summary */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "bold", color: "#FF6B35" }}
                          >
                            ₹{order.total_amount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Amount
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mb: 0.5,
                            }}
                          >
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(order.order_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Order Date
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Order Details Accordion */}
                      <Accordion
                        sx={{
                          background: "transparent",
                          boxShadow: "none",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon sx={{ color: "#008080" }} />
                          }
                          sx={{
                            px: 0,
                            "& .MuiAccordionSummary-content": {
                              margin: "8px 0",
                            },
                          }}
                        >
                          <Typography
                            sx={{ color: "#FF6B35", fontWeight: "bold" }}
                          >
                            View Order Details
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0 }}>
                          {/* Delivery Info */}
                          <Box sx={{ mb: 3 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    background: "rgba(0,128,128,0.05)",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "#008080",
                                      mb: 1,
                                    }}
                                  >
                                    Delivery Date
                                  </Typography>
                                  <Typography variant="body2">
                                    {order.reachby_date || "Not specified"}
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    background: "rgba(255,107,53,0.05)",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "#FF6B35",
                                      mb: 1,
                                    }}
                                  >
                                    Remarks
                                  </Typography>
                                  <Typography variant="body2">
                                    {order.remark || "No remarks"}
                                  </Typography>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* POD Section */}
                          <Box sx={{ mb: 3 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: "bold",
                                mb: 2,
                                color: "#008080",
                              }}
                            >
                              Proof of Delivery
                            </Typography>
                            {order.pod_image ? (
                              <Paper
                                sx={{
                                  p: 1,
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    transform: "scale(1.02)",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                                  },
                                }}
                                onClick={() =>
                                  handleImageClick(order.pod_image)
                                }
                              >
                                <Box sx={{ position: "relative" }}>
                                  <Box
                                    component="img"
                                    src={`data:image/png;base64,${order.pod_image}`}
                                    alt="Proof of Delivery"
                                    sx={{
                                      width: "100%",
                                      maxHeight: "200px",
                                      objectFit: "contain",
                                      borderRadius: 1,
                                    }}
                                  />
                                  <IconButton
                                    sx={{
                                      position: "absolute",
                                      top: 8,
                                      right: 8,
                                      background: "rgba(0,0,0,0.5)",
                                      color: "white",
                                      "&:hover": {
                                        background: "rgba(0,0,0,0.7)",
                                      },
                                    }}
                                  >
                                    <ZoomInIcon />
                                  </IconButton>
                                </Box>
                              </Paper>
                            ) : (
                              <Paper
                                sx={{
                                  p: 3,
                                  textAlign: "center",
                                  background: "rgba(0,0,0,0.02)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No proof of delivery available
                                </Typography>
                              </Paper>
                            )}
                          </Box>

                          {/* Products Table */}
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: "bold",
                                mb: 2,
                                color: "#008080",
                              }}
                            >
                              Product Details
                            </Typography>
                            <Paper sx={{ overflow: "hidden" }}>
                              <Table>
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      background:
                                        "linear-gradient(45deg, #008080, #20B2AA)",
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        fontWeight: "bold",
                                        color: "white",
                                      }}
                                    >
                                      Product Name
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: "bold",
                                        color: "white",
                                      }}
                                    >
                                      Buy Qty
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: "bold",
                                        color: "white",
                                      }}
                                    >
                                      Offer Qty
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: "bold",
                                        color: "white",
                                      }}
                                    >
                                      Net Qty
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {order.products.map((product, idx) => (
                                    <TableRow
                                      key={product.product_id}
                                      sx={{
                                        "&:nth-of-type(odd)": {
                                          backgroundColor:
                                            "rgba(0,128,128,0.02)",
                                        },
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(0,128,128,0.05)",
                                        },
                                      }}
                                    >
                                      <TableCell sx={{ fontWeight: "medium" }}>
                                        {product.product_name}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={product.buy_quantity}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={product.offer_quantity}
                                          size="small"
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={product.net_quantity}
                                          size="small"
                                          sx={{
                                            background:
                                              "linear-gradient(45deg, #008080, #20B2AA)",
                                            color: "white",
                                            fontWeight: "bold",
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 3,
                }}
              >
                <ShoppingBagIcon
                  sx={{ fontSize: 64, color: "#008080", mb: 2 }}
                />
                <Typography variant="h5" sx={{ color: "#008080", mb: 1 }}>
                  No Orders Found
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {searchTerm
                    ? "No orders match your search criteria."
                    : "You haven't placed any orders yet."}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Image Modal */}
        <Dialog
          open={!!selectedImage}
          onClose={handleCloseImage}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ color: "#008080" }}>
              Proof of Delivery
            </Typography>
            <IconButton onClick={handleCloseImage}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedImage && (
              <Box
                component="img"
                src={`data:image/png;base64,${selectedImage}`}
                alt="Proof of Delivery"
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 2,
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default OrderHistory;
